import * as React from "react";
import { ChevronRight16Regular, Folder20Regular, FolderAdd20Regular } from "@fluentui/react-icons";
import { documentService } from "../../../services/documentService";
import { kundenakteOrdnerService } from "../../../services/kundenakteOrdnerService";

/** Kategorie, unter der die Akte ihren Ordnerbaum führt. */
const KUNDENAKTE = "Alte Kundenakte";

/** Ordnerpfad relativ zur Akte: "" = Wurzel, sonst mit „/" am Ende. */
export const ordnerSegmente = (pfad: string): string[] => pfad.split("/").filter(Boolean);
export const ordnerTitel = (pfad: string, wurzel = "Kundenakte"): string => ordnerSegmente(pfad).pop() ?? wurzel;

/**
 * Die unmittelbaren Unterordner einer Ebene: Ordner mit Dateien (aus dem
 * Ordner-Listing) plus leer angelegte (`angelegt`: Pfade relativ zur Akte, mit
 * „/" am Ende). Alphabetisch, ohne Dubletten.
 */
export function unterordner(pfad: string, mitDateien: readonly string[], angelegt: readonly string[]): string[] {
    const praefix = pfad.toLowerCase();
    const leer = angelegt
        .filter((p) => p.toLowerCase().startsWith(praefix) && p.length > pfad.length)
        .map((p) => p.slice(pfad.length).split("/")[0])
        .filter(Boolean);
    const gesehen = new Map<string, string>();
    for (const name of [...mitDateien, ...leer]) {
        const key = name.trim().toLowerCase();
        if (key && !gesehen.has(key)) gesehen.set(key, name.trim());
    }
    return [...gesehen.values()].sort((a, b) => a.localeCompare(b, "de", { numeric: true, sensitivity: "base" }));
}

/** Zeichen, die in einem Ordnernamen nichts verloren haben, entfernen. */
const sauber = (name: string): string =>
    name.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().replace(/[. ]+$/, "").slice(0, 120);

/**
 * Durch die Ordner der Kundenakte eines Kontakts navigieren — beliebig tief.
 * Der GEÖFFNETE Ordner ist die Auswahl: wer einen Ordner als Ziel braucht
 * (Mail ablegen, Datei verschieben), geht hinein. Mit `neuAnlegen` lässt sich
 * an jeder Stelle ein Ordner anlegen; er wird danach sofort geöffnet.
 *
 * Bewusst ein eigenes kleines Bauteil statt des großen Explorers aus dem
 * Reiter „Dokumente": dort hängen Dateiliste, Papierkorb, Verbund- und
 * KMVS-Ordner dran, die für eine Ordnerwahl nur stören.
 */
export const OrdnerNavigator: React.FC<{
    contactId: number;
    pfad: string;
    onPfad: (pfad: string) => void;
    /** „Neuer Ordner" anbieten (legt im geöffneten Ordner an). */
    neuAnlegen?: boolean;
    /** Ändert sich der Wert, wird neu geladen (z. B. nach einer Akten-Zuweisung). */
    neuLadenKey?: number;
    /** Diese Ordner nicht anbieten (beim Verschieben: der Ordner selbst). */
    ausblenden?: (pfad: string) => boolean;
    /** Name der Wurzel in den Brotkrumen. */
    wurzelName?: string;
    maxHoehe?: number;
}> = ({ contactId, pfad, onPfad, neuAnlegen = false, neuLadenKey = 0, ausblenden, wurzelName = "Kundenakte", maxHoehe = 240 }) => {
    const [ordner, setOrdner] = React.useState<string[]>([]);
    const [laedt, setLaedt] = React.useState(true);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [neuOffen, setNeuOffen] = React.useState(false);
    const [neuName, setNeuName] = React.useState("");
    const [legtAn, setLegtAn] = React.useState(false);
    const [tick, setTick] = React.useState(0);

    React.useEffect(() => {
        const ctrl = new AbortController();
        setLaedt(true);
        setFehler(null);
        Promise.all([
            documentService.listFolders({ contactId, prefix: pfad, category: KUNDENAKTE }, ctrl.signal),
            kundenakteOrdnerService.liste(contactId, ctrl.signal),
        ])
            .then(([mitDateien, angelegt]) => {
                if (ctrl.signal.aborted) return;
                setOrdner(unterordner(pfad, mitDateien.map((f) => f.name), angelegt));
            })
            .catch(() => {
                if (!ctrl.signal.aborted) setFehler("Die Ordner konnten nicht geladen werden.");
            })
            .finally(() => {
                if (!ctrl.signal.aborted) setLaedt(false);
            });
        return () => ctrl.abort();
    }, [contactId, pfad, neuLadenKey, tick]);

    React.useEffect(() => {
        setNeuOffen(false);
        setNeuName("");
    }, [pfad]);

    const anlegen = async () => {
        const name = sauber(neuName);
        if (!name || legtAn) return;
        if (ordner.some((o) => o.toLowerCase() === name.toLowerCase())) {
            onPfad(`${pfad}${ordner.find((o) => o.toLowerCase() === name.toLowerCase())}/`);
            return;
        }
        setLegtAn(true);
        setFehler(null);
        try {
            await kundenakteOrdnerService.anlegen(contactId, `${pfad}${name}`);
            setTick((t) => t + 1);
            onPfad(`${pfad}${name}/`);
        } catch (e) {
            setFehler(e instanceof Error && e.message ? e.message : "Der Ordner konnte nicht angelegt werden.");
        } finally {
            setLegtAn(false);
        }
    };

    const segmente = ordnerSegmente(pfad);
    const sichtbar = ordner.filter((o) => !ausblenden?.(`${pfad}${o}/`));

    return (
        <div style={rahmen}>
            <div style={krumen} aria-label="Ordnerpfad">
                <button type="button" style={segmente.length ? krume : krumeAktiv} onClick={() => onPfad("")}>
                    {wurzelName}
                </button>
                {segmente.map((s, i) => {
                    const ziel = `${segmente.slice(0, i + 1).join("/")}/`;
                    const letzte = i === segmente.length - 1;
                    return (
                        <React.Fragment key={ziel}>
                            <ChevronRight16Regular style={{ color: "#9a8c95", flexShrink: 0 }} />
                            <button type="button" style={letzte ? krumeAktiv : krume} onClick={() => onPfad(ziel)}>
                                {s}
                            </button>
                        </React.Fragment>
                    );
                })}
            </div>

            <div style={{ ...liste, maxHeight: maxHoehe }}>
                {laedt ? (
                    <div style={hinweis}>Ordner werden geladen…</div>
                ) : sichtbar.length === 0 ? (
                    <div style={hinweis}>Hier gibt es keine Unterordner.</div>
                ) : (
                    sichtbar.map((o) => (
                        <button key={o} type="button" style={zeile} onClick={() => onPfad(`${pfad}${o}/`)} title={`„${o}“ öffnen`}>
                            <Folder20Regular style={{ color: "#b98a2b", flexShrink: 0 }} />
                            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o}</span>
                            <ChevronRight16Regular style={{ color: "#9a8c95", flexShrink: 0 }} />
                        </button>
                    ))
                )}
            </div>

            {neuAnlegen &&
                (neuOffen ? (
                    <div style={{ display: "flex", gap: 6, padding: 8, borderTop: `1px solid ${LINE}` }}>
                        <input
                            autoFocus
                            value={neuName}
                            onChange={(e) => setNeuName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") void anlegen();
                                if (e.key === "Escape") setNeuOffen(false);
                            }}
                            placeholder="Name des neuen Ordners…"
                            aria-label="Name des neuen Ordners"
                            maxLength={120}
                            style={eingabe}
                        />
                        <button type="button" style={knopf} disabled={!sauber(neuName) || legtAn} onClick={() => void anlegen()}>
                            {legtAn ? "Legt an…" : "Anlegen"}
                        </button>
                    </div>
                ) : (
                    <button type="button" style={neuKnopf} onClick={() => setNeuOffen(true)}>
                        <FolderAdd20Regular /> Neuer Ordner in „{ordnerTitel(pfad, wurzelName)}“
                    </button>
                ))}
            {fehler && <div role="alert" style={fehlerZeile}>{fehler}</div>}
        </div>
    );
};

const AUB = "#4d2051";
const LINE = "#ece7f0";
const rahmen: React.CSSProperties = { border: `1px solid ${LINE}`, borderRadius: 10, overflow: "hidden", background: "#fff", fontFamily: "'Barlow', 'Segoe UI', sans-serif" };
const krumen: React.CSSProperties = { display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", padding: "8px 10px", borderBottom: `1px solid ${LINE}`, background: "#fbf9fc" };
const krume: React.CSSProperties = { border: "none", background: "transparent", color: AUB, cursor: "pointer", fontSize: 13, fontFamily: "inherit", padding: "2px 4px", borderRadius: 6, textDecoration: "underline" };
const krumeAktiv: React.CSSProperties = { ...krume, color: "#241522", fontWeight: 800, textDecoration: "none", cursor: "default" };
const liste: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 1, overflowY: "auto", padding: 4, minHeight: 56 };
const zeile: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", border: "none", background: "transparent", borderRadius: 8, padding: "8px 10px", cursor: "pointer", color: "#241522", fontSize: 13.5, fontFamily: "inherit" };
const hinweis: React.CSSProperties = { color: "#6b5a66", fontSize: 13, padding: "10px 8px" };
const eingabe: React.CSSProperties = { flex: 1, minWidth: 0, boxSizing: "border-box", padding: "7px 10px", border: `1px solid ${LINE}`, borderRadius: 8, fontSize: 13.5, fontFamily: "inherit", outline: "none", color: "#241522" };
const knopf: React.CSSProperties = { border: `1px solid ${AUB}`, background: AUB, color: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
const neuKnopf: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, width: "100%", border: "none", borderTop: `1px solid ${LINE}`, background: "transparent", color: AUB, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", padding: "9px 12px", textAlign: "left" };
const fehlerZeile: React.CSSProperties = { padding: "6px 10px", background: "#fdf0f0", color: "#8a2626", fontSize: 12.5 };

export default OrdnerNavigator;
